import React, { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button, Space, Slider } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { volcano } from '@ant-design/colors';

const ACTIVE_COLOR = volcano.primary;

// Fix default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom selected marker icon (volcano/orange)
const selectedIcon = new L.Icon({
  iconUrl: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 25 41'%3E%3Cpath fill='${encodeURIComponent(ACTIVE_COLOR)}' d='M12.5 0C5.6 0 0 5.6 0 12.5c0 12.5 12.5 28 12.5 28s12.5-15.5 12.5-28C25 5.6 19.4 0 12.5 0zm0 18.8c-3.5 0-6.3-2.8-6.3-6.3s2.8-6.3 6.3-6.3 6.3 2.8 6.3 6.3-2.8 6.3-6.3 6.3z'/%3E%3C/svg%3E`,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41]
});

// Custom unselected marker icon (grey)
const unselectedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map clicks
const MapClickHandler = ({ onMapClick }) => {
  const map = useMap();
  
  React.useEffect(() => {
    const handleClick = (e) => {
      onMapClick(e);
    };
    map.on('click', handleClick);
    return () => map.off('click', handleClick);
  }, [map, onMapClick]);
  
  return null;
};

// Component to fit map bounds to markers (only on initial load, not when radius active)
const FitBounds = ({ locations, radiusCenter }) => {
  const map = useMap();
  
  useMemo(() => {
    // Only fit bounds if there's no radius active and we have locations
    if (locations.length > 0 && !radiusCenter) {
      const bounds = locations.map(loc => [
        parseFloat(loc.gps_latitude),
        parseFloat(loc.gps_longitude)
      ]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map, radiusCenter]);
  
  return null;
};

const LocationMapSelector = ({ locations, selectedLocations, onToggleLocation }) => {
  // Filter locations that have GPS coordinates
  const locationsWithGPS = useMemo(() => 
    locations.filter(loc => loc.gps_latitude && loc.gps_longitude),
    [locations]
  );

  const [radiusCenter, setRadiusCenter] = useState(null);
  const [radiusMeters, setRadiusMeters] = useState(5000); // 5km default

  // Calculate distance between two coordinates in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Filter locations by radius if a radius is set
  const locationsInRadius = useMemo(() => {
    if (!radiusCenter) return locationsWithGPS;
    
    return locationsWithGPS.filter(loc => {
      const distance = calculateDistance(
        radiusCenter.lat,
        radiusCenter.lng,
        parseFloat(loc.gps_latitude),
        parseFloat(loc.gps_longitude)
      );
      return distance <= radiusMeters;
    });
  }, [locationsWithGPS, radiusCenter, radiusMeters]);

  const handleMapClick = (e) => {
    setRadiusCenter(e.latlng);
  };

  const clearRadius = () => {
    setRadiusCenter(null);
  };

  // Auto-select locations when radius is set or changed
  React.useEffect(() => {
    if (radiusCenter) {
      // Get all location names within radius
      const locationsToSelect = locationsInRadius.map(loc => loc.name);
      
      // Select all locations in radius (if not already selected)
      locationsToSelect.forEach(locName => {
        if (!selectedLocations.includes(locName)) {
          onToggleLocation(locationsInRadius.find(loc => loc.name === locName));
        }
      });
    }
  }, [radiusCenter, radiusMeters, locationsInRadius, locations, onToggleLocation]);

  if (locationsWithGPS.length === 0) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        color: '#888'
      }}>
        No locations with GPS coordinates available
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Radius Controls */}
      <div style={{
        backgroundColor: '#2a2a2a',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #444',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{ color: '#aaa', fontSize: '12px', whiteSpace: 'nowrap' }}>
          RADIUS:
        </div>
        {radiusCenter ? (
          <>
            <div style={{ color: ACTIVE_COLOR, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {radiusMeters}m
            </div>
            <Slider
              min={100}
              max={100000}
              step={500}
              value={radiusMeters}
              onChange={setRadiusMeters}
              marks={{
                100: '100m',
                10000: '10km',
                100000: '100km'
              }}
              style={{ flex: 1, minWidth: '200px' }}
            />
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={clearRadius}
              size="small"
            >
              Clear
            </Button>
          </>
        ) : (
          <div style={{ color: '#888', fontSize: '12px', flex: 1 }}>
            Click on the map to set a radius center
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ height: '500px', width: '100%', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#1a1a1a' }}>
        <MapContainer
          style={{ height: '100%', width: '100%', backgroundColor: '#1a1a1a' }}
          center={[51.5074, -0.1278]}
          zoom={10}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} />
          <FitBounds locations={locationsWithGPS} radiusCenter={radiusCenter} />
          {radiusCenter && (
            <Circle
              center={[radiusCenter.lat, radiusCenter.lng]}
              radius={radiusMeters}
              pathOptions={{
                color: ACTIVE_COLOR,
                weight: 2,
                opacity: 0.7,
                fillOpacity: 0.1,
                dashArray: '8 4'
              }}
            />
          )}
          {/* Show all locations with GPS, highlight those in radius */}
          {locationsWithGPS.map(location => {
            const isSelected = selectedLocations.includes(location.name);
            
            // Use blue icon if selected, light blue if in radius, grey otherwise
            let icon = unselectedIcon;
            if (isSelected) {
              icon = selectedIcon;
            }
            
            return (
              <Marker
                key={location.id}
                position={[parseFloat(location.gps_latitude), parseFloat(location.gps_longitude)]}
                icon={icon}
                eventHandlers={{
                  click: () => onToggleLocation(location)
                }}
                riseOnHover={true}
              >
                <Tooltip direction="top" offset={[0, -30]} opacity={0.9}>
                  <span style={{ color: '#000' }}>{location.name}</span>
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Info */}
      {radiusCenter && (
        <div style={{ 
          backgroundColor: '#2a2a2a', 
          padding: '8px 12px', 
          borderRadius: '6px',
          fontSize: '12px',
          color: '#aaa'
        }}>
          Showing {locationsInRadius.length} location{locationsInRadius.length !== 1 ? 's' : ''} in radius
        </div>
      )}
    </div>
  );
};

export default LocationMapSelector;
